import { useSyncExternalStore } from 'react';

/**
 * The skills inserted last from the composer's picker, on this device (most recent first): the
 * + lists them as its "Recent skills".
 */
const STORAGE_KEY = 'crowdex:composer-recent-skills';
const MAX_RECENT = 8;
const listeners = new Set<() => void>();
let cached: { raw: string | null; ids: readonly string[] } | null = null;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The ids (`skill.uri`) of the recent skills, most recent first. */
export function readRecentSkills(): readonly string[] {
  const raw = storage()?.getItem(STORAGE_KEY) ?? null;
  if (cached !== null && cached.raw === raw) return cached.ids;
  let ids: readonly string[] = [];
  try {
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    ids = Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string').slice(0, MAX_RECENT)
      : [];
  } catch {
    ids = [];
  }
  cached = { raw, ids };
  return ids;
}

/** Puts a skill first among the recent ones. */
export function recordRecentSkill(id: string): void {
  const next = [id, ...readRecentSkills().filter((candidate) => candidate !== id)].slice(
    0,
    MAX_RECENT,
  );
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A full or blocked storage keeps the list as it was.
  }
  for (const listener of listeners) listener();
}

const EMPTY: readonly string[] = [];

/** The recent skills, kept up to date as skills are inserted. */
export function useRecentSkills(): readonly string[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    readRecentSkills,
    () => EMPTY,
  );
}
